import { Injectable } from 'framework/di';
import { Signal, signal } from 'framework/store/signal';

import { type CacheEntry, Callback, EventName, FilterStates } from './filter.model';

/**
 * Store for managing filter state, URL parameter mappings, and filter cache.
 *
 * This store manages filter-related state including URL parameter shortening,
 * request caching, and state change notifications. It can be extended by
 * Technical Support teams to customize filter behavior for specific shops.
 *
 * @remarks
 * The store implements an LRU (Least Recently Used) cache with a default size
 * of 50 entries to optimize filter performance while managing memory usage.
 * State changes trigger notifications to all registered watchers.
 *
 * @example
 * Extend to customize cache size per shop:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterStore', (FilterStore) => {
 *   return class CustomFilterStore extends FilterStore {
 *     setCacheEntry(key, value) {
 *       const timestamp = new Date().getTime();
 *       this.cache.value.set(key, { ...value, timestamp });
 *       // Use larger cache for premium shops
 *       const maxCacheSize = this.isPremiumShop() ? 100 : 50;
 *       // Implement custom eviction logic
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class FilterStore {
  /**
   * Reactive signal containing the current filter state.
   *
   * @remarks
   * Use `getState()` or `watch()` methods to access or observe state changes.
   * Direct mutation of this signal should be avoided; use `setState()` instead.
   */
  state: Signal<FilterStates>;
  /**
   * Internal LRU cache for filter results.
   *
   * @internal
   */
  private cache: Signal<Map<string, CacheEntry>> = signal(new Map());
  /**
   * Map of event names to their registered callback functions.
   *
   * @remarks
   * For internal event system management. Use public methods to interact with events.
   */
  events: Signal<Map<EventName, Callback[]>> = signal(new Map());
  private watchers: Map<number, { key?: keyof FilterStates; callback: (value: any) => void }> =
    new Map();
  private watcherId = 0;

  constructor() {
    this.state = signal<FilterStates>({
      latestRequestKey: null,
      shortParamsMap: new Map(),
      longParamsMap: new Map(),
      processedLabels: new WeakSet(),
    } as FilterStates);
  }

  /**
   * Updates the filter state with new values and notifies all watchers.
   *
   * This method merges the provided partial state with the current state and
   * triggers notifications to all registered watchers. Override this method to
   * add custom validation or transformation logic before state updates.
   *
   * @param newState - Partial state object with properties to update.
   * Only provided properties will be updated; others remain unchanged.
   *
   * @example
   * Extend to add state validation:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterStore', (FilterStore) => {
   *   return class CustomFilterStore extends FilterStore {
   *     setState(newState) {
   *       // Validate before updating
   *       if (newState.latestRequestKey && !this.isValidKey(newState.latestRequestKey)) {
   *         console.warn('Invalid request key');
   *         return;
   *       }
   *       super.setState(newState);
   *     }
   *   };
   * });
   * ```
   */
  setState(newState: Partial<FilterStates>): void {
    this.state.value = { ...this.state.value, ...newState };
    this.notifyWatchers();
  }

  /**
   * Subscribes to filter state changes.
   *
   * Watch the entire state for any changes. Use this when you need to react
   * to any state update regardless of which property changed.
   *
   * @param callback - Function called with the entire state when any property changes
   * @returns Unsubscribe function to stop watching state changes
   *
   * @example
   * Basic usage:
   * ```typescript
   * const unsubscribe = filterStore.watch((state) => {
   *   console.log('State changed:', state);
   * });
   * // Later, stop watching
   * unsubscribe();
   * ```
   */
  watch(callback: (state: FilterStates) => void): () => void;
  /**
   * Subscribes to changes of a specific filter state property.
   *
   * Watch a single property for changes. More efficient than watching the entire
   * state when you only care about specific properties.
   *
   * @param key - The state property name to watch (e.g., 'latestRequestKey')
   * @param callback - Function called with the new property value when it changes
   * @returns Unsubscribe function to stop watching this property
   *
   * @example
   * Watch a specific property:
   * ```typescript
   * const unsubscribe = filterStore.watch('latestRequestKey', (key) => {
   *   console.log('Latest request key changed to:', key);
   * });
   * ```
   *
   * @example
   * Extend to add custom watcher behavior:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterStore', (FilterStore) => {
   *   return class CustomFilterStore extends FilterStore {
   *     watch(keyOrCallback, callback) {
   *       const unsubscribe = super.watch(keyOrCallback, callback);
   *       console.log('New watcher registered');
   *       return () => {
   *         console.log('Watcher removed');
   *         unsubscribe();
   *       };
   *     }
   *   };
   * });
   * ```
   */
  watch<K extends keyof FilterStates>(
    key: K,
    callback: (value: FilterStates[K]) => void
  ): () => void;
  watch<K extends keyof FilterStates>(
    keyOrCallback: K | ((state: FilterStates) => void),
    callback?: (value: FilterStates[K]) => void
  ): () => void {
    const id = this.watcherId++;

    if (typeof keyOrCallback === 'function') {
      // Watch entire state
      this.watchers.set(id, { callback: keyOrCallback });
    } else {
      // Watch specific key
      this.watchers.set(id, { key: keyOrCallback, callback: callback! });
    }

    // Return unsubscribe function
    return () => {
      this.watchers.delete(id);
    };
  }

  /**
   * Notifies all registered watchers of state changes.
   *
   * This method is called automatically after `setState()`. Override this method
   * to customize notification behavior, such as debouncing notifications or
   * filtering which watchers to notify based on shop-specific logic.
   *
   * @remarks
   * This method iterates through all watchers and calls their callbacks with
   * either the entire state or specific property values depending on how they
   * were registered.
   *
   * @example
   * Debounce notifications for better performance:
   * ```typescript
   * protected notifyWatchers() {
   *   clearTimeout(this.notifyTimeout);
   *   this.notifyTimeout = setTimeout(() => {
   *     super.notifyWatchers();
   *   }, 100);
   * }
   * ```
   */
  protected notifyWatchers(): void {
    this.watchers.forEach((watcher) => {
      if (watcher.key !== undefined) {
        // Notify watchers of specific key
        watcher.callback(this.state.value[watcher.key]);
      } else {
        // Notify watchers of entire state
        watcher.callback(this.state.value);
      }
    });
  }

  /**
   * Initializes URL parameter shortening maps from the provided configuration.
   *
   * Creates bidirectional mappings between long and short parameter names for
   * URL optimization. This reduces URL length while maintaining readability in
   * the application code.
   *
   * @param shortenUrlParamList - Array of mapping rules in format 'long_key:short_key'.
   * For example, ['productId:pid', 'category:cat']. Invalid formats are silently
   * ignored. Defaults to empty array if not provided.
   *
   * @remarks
   * The method parses each rule by finding the last ':' character, allowing
   * colons in the long key name. Empty keys are skipped. Both maps are updated
   * atomically via `setState()` to ensure consistency.
   *
   * @example
   * Basic usage:
   * ```typescript
   * filterStore.initShortenMaps([
   *   'productId:pid',
   *   'categoryName:cat',
   *   'priceRange:pr'
   * ]);
   * ```
   *
   * @example
   * Extend to add custom URL shortening rules:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterStore', (FilterStore) => {
   *   return class CustomFilterStore extends FilterStore {
   *     initShortenMaps(shortenUrlParamList = []) {
   *       // Add shop-specific default mappings
   *       const customMappings = [
   *         ...shortenUrlParamList,
   *         'shopCustomField:scf'
   *       ];
   *       super.initShortenMaps(customMappings);
   *     }
   *   };
   * });
   * ```
   */
  initShortenMaps(shortenUrlParamList: string[] = []): void {
    const shortParamsMap = new Map<string, string>();
    const longParamsMap = new Map<string, string>();

    shortenUrlParamList.forEach((rule) => {
      if (typeof rule !== 'string') return;
      const idx = rule.lastIndexOf(':');
      if (idx === -1) return;

      const longKey = rule.slice(0, idx).trim();
      const shortKey = rule.slice(idx + 1).trim();

      if (!longKey || !shortKey) return;

      shortParamsMap.set(longKey, shortKey);
      longParamsMap.set(shortKey, longKey);
    });

    this.setState({ shortParamsMap, longParamsMap });
  }

  /**
   * Retrieves the entire current filter state.
   *
   * @returns A shallow copy of the complete state object to prevent
   * accidental mutations of the internal state
   *
   * @example
   * ```typescript
   * const state = filterStore.getState();
   * console.log(state.latestRequestKey, state.shortParamsMap);
   * ```
   */
  getState(): FilterStates;
  /**
   * Retrieves a specific property from the current filter state.
   *
   * @param key - The name of the state property to retrieve
   * @returns The current value of the specified property
   *
   * @example
   * ```typescript
   * const requestKey = filterStore.getState('latestRequestKey');
   * const shortMap = filterStore.getState('shortParamsMap');
   * ```
   */
  getState<K extends keyof FilterStates>(key: K): FilterStates[K];
  getState<K extends keyof FilterStates>(key?: K): FilterStates | FilterStates[K] {
    if (key !== undefined) {
      return this.state.value[key];
    }
    // Return a shallow copy to prevent accidental mutations
    return { ...this.state.value };
  }

  /**
   * Retrieves a cached filter result by its key.
   *
   * @param key - The unique cache key identifying the filter result
   * @returns The cached entry if found, null if the key doesn't exist in cache
   *
   * @example
   * ```typescript
   * const cached = filterStore.getCacheEntry('filter-v1-cat:shoes-sort:price');
   * if (cached) {
   *   console.log('Cache hit:', cached.data);
   * }
   * ```
   */
  getCacheEntry(key: string): CacheEntry | null {
    return this.cache.value.get(key) || null;
  }

  /**
   * Stores a filter result in the cache with automatic LRU eviction.
   *
   * Adds or updates a cache entry with the current timestamp. When the cache
   * reaches its maximum size (50 entries), the oldest entry is automatically
   * removed using LRU (Least Recently Used) eviction strategy.
   *
   * @param key - The unique cache key identifying this filter result
   * @param value - The cache entry data (timestamp is added automatically)
   *
   * @remarks
   * The LRU eviction iterates through all cache entries to find the oldest one.
   * For very large caches, consider overriding this method with a more efficient
   * data structure like a linked list or using a library implementation.
   *
   * @example
   * Basic usage:
   * ```typescript
   * filterStore.setCacheEntry('filter-v1-cat:shoes', {
   *   data: { products: [...], total: 42 },
   *   params: { category: 'shoes' }
   * });
   * ```
   *
   * @example
   * Extend to customize cache size per shop:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterStore', (FilterStore) => {
   *   return class CustomFilterStore extends FilterStore {
   *     setCacheEntry(key, value) {
   *       const timestamp = new Date().getTime();
   *       this.cache.value.set(key, { ...value, timestamp });
   *
   *       // Use larger cache for premium shops
   *       const maxCacheSize = this.getMaxCacheSize();
   *       if (this.cache.value.size >= maxCacheSize) {
   *         // Custom eviction logic
   *       }
   *     }
   *
   *     getMaxCacheSize() {
   *       return window.shopTier === 'premium' ? 100 : 50;
   *     }
   *   };
   * });
   * ```
   */
  setCacheEntry(key: string, value: Omit<CacheEntry, 'timestamp'>): void {
    const timestamp = new Date().getTime();
    this.cache.value.set(key, { ...value, timestamp });

    // Implement LRU cache eviction when max size is reached
    const maxCacheSize = 50;
    if (this.cache.value.size >= maxCacheSize) {
      // Find the oldest entry by timestamp
      let oldestKey: string | null = null;
      let oldestTimestamp = Infinity;

      this.cache.value.forEach((entry, cacheKey) => {
        if (entry.timestamp < oldestTimestamp) {
          oldestTimestamp = entry.timestamp;
          oldestKey = cacheKey;
        }
      });

      // Delete the oldest entry
      if (oldestKey) {
        this.cache.value.delete(oldestKey);
      }
    }
  }

  /**
   * Checks whether a specific cache entry exists.
   *
   * @param key - The cache key to check for existence
   * @returns True if the cache contains an entry with this key, false otherwise
   *
   * @example
   * ```typescript
   * if (filterStore.hasCacheEntry('filter-v1-cat:shoes')) {
   *   const cached = filterStore.getCacheEntry('filter-v1-cat:shoes');
   * } else {
   *   // Fetch fresh data
   * }
   * ```
   */
  hasCacheEntry(key: string): boolean {
    return this.cache.value.has(key);
  }

  /**
   * Clears all cached filter results.
   *
   * Removes all entries from the cache. Use this when filter configuration changes
   * or when you need to force fresh data fetching for all subsequent requests.
   *
   * @example
   * Clear cache when settings change:
   * ```typescript
   * filterStore.clearCache();
   * ```
   *
   * @example
   * Extend to add logging when cache is cleared:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterStore', (FilterStore) => {
   *   return class CustomFilterStore extends FilterStore {
   *     clearCache() {
   *       console.log('Clearing filter cache');
   *       super.clearCache();
   *     }
   *   };
   * });
   * ```
   */
  clearCache(): void {
    this.cache.value.clear();
  }
}
